/**
 * Computes per-item set diffs for collection fields (services, medications, etc.)
 *
 * Given two JSON arrays (old and new), returns which items were added,
 * removed, or unchanged. Uses IDs for comparison when available,
 * falling back to display names.
 */
import { type FieldChange } from "./types";

export type ItemDiffStatus = "added" | "removed" | "unchanged";

export interface ItemDiff {
  /** The ID (from valueId arrays) or display name if no ID available */
  id: string;
  /** Display name from the value arrays */
  displayName: string;
  status: ItemDiffStatus;
}

function parseJsonArray(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Compute a set diff between old and new collection values.
 *
 * Uses the ID arrays (oldValueId/newValueId) for comparison when available,
 * since display names can be ambiguous. Falls back to display name arrays.
 *
 * Returns items in order: unchanged first, then added, then removed.
 */
export function computeCollectionDiff(change: FieldChange): ItemDiff[] {
  const hasIds = change.oldValueId || change.newValueId;

  if (hasIds) {
    return computeDiffWithIds(change);
  }
  return computeDiffByName(change);
}

function computeDiffWithIds(change: FieldChange): ItemDiff[] {
  const oldIds = parseJsonArray(change.oldValueId);
  const oldNames = parseJsonArray(change.oldValue);
  const newIds = parseJsonArray(change.newValueId);
  const newNames = parseJsonArray(change.newValue);

  const oldIdSet = new Set(oldIds);
  const newIdSet = new Set(newIds);

  // Build ID→name maps
  const idToName = new Map<string, string>();
  oldIds.forEach((id, i) => idToName.set(id, oldNames[i] ?? id));
  newIds.forEach((id, i) => idToName.set(id, newNames[i] ?? id));

  const items: ItemDiff[] = [];

  // Unchanged: in both old and new
  for (const id of newIds) {
    if (oldIdSet.has(id)) {
      items.push({
        id,
        displayName: idToName.get(id) ?? id,
        status: "unchanged",
      });
    }
  }

  // Added: in new but not old
  for (const id of newIds) {
    if (!oldIdSet.has(id)) {
      items.push({ id, displayName: idToName.get(id) ?? id, status: "added" });
    }
  }

  // Removed: in old but not new
  for (const id of oldIds) {
    if (!newIdSet.has(id)) {
      items.push({
        id,
        displayName: idToName.get(id) ?? id,
        status: "removed",
      });
    }
  }

  return items;
}

/**
 * Compute a set diff for MEASUREMENTS, which are serialized as JSON objects
 * `[{"measurement_field": "temp", "display_unit": "F"}, ...]`
 * rather than simple string arrays. Uses `measurement_field` as the key.
 */
export function computeMeasurementDiff(change: FieldChange): ItemDiff[] {
  const oldItems = parseMeasurementArray(change.oldValue);
  const newItems = parseMeasurementArray(change.newValue);
  const oldKeys = new Set(oldItems.map((m) => m.measurement_field));
  const newKeys = new Set(newItems.map((m) => m.measurement_field));

  const items: ItemDiff[] = [];

  for (const m of newItems) {
    items.push({
      id: m.measurement_field,
      displayName: m.measurement_field,
      status: oldKeys.has(m.measurement_field) ? "unchanged" : "added",
    });
  }

  for (const m of oldItems) {
    if (!newKeys.has(m.measurement_field)) {
      items.push({
        id: m.measurement_field,
        displayName: m.measurement_field,
        status: "removed",
      });
    }
  }

  return items;
}

function parseMeasurementArray(
  value: string | null,
): { measurement_field: string; display_unit?: string }[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown[];
    return parsed.filter(
      (m): m is { measurement_field: string; display_unit?: string } =>
        typeof m === "object" && m !== null && "measurement_field" in m,
    );
  } catch {
    return [];
  }
}

function computeDiffByName(change: FieldChange): ItemDiff[] {
  const oldNames = parseJsonArray(change.oldValue);
  const newNames = parseJsonArray(change.newValue);
  const oldSet = new Set(oldNames);
  const newSet = new Set(newNames);

  const items: ItemDiff[] = [];

  for (const name of newNames) {
    items.push({
      id: name,
      displayName: name,
      status: oldSet.has(name) ? "unchanged" : "added",
    });
  }

  for (const name of oldNames) {
    if (!newSet.has(name)) {
      items.push({ id: name, displayName: name, status: "removed" });
    }
  }

  return items;
}
